import { useEffect, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import FormField from "../components/FormField";
import Button from "../components/Button";
import Alert from "../components/Alert";
import QrCode from "../components/QrCode";
import { cerberus, errorMessage } from "../api/cerberus";
import { readAccessToken } from "../lib/session";
import type { MfaEnrollResponse } from "../api/types";

export default function MfaEnroll(): JSX.Element {
  const token = readAccessToken();
  const [enrollment, setEnrollment] = useState<MfaEnrollResponse | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    cerberus
      .mfaEnroll(token)
      .then((r) => {
        if (!cancelled) setEnrollment(r);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(errorMessage(e, "mfa enroll failed"));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!token) return <Navigate to="/login" replace />;

  async function onVerify(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (!token) return;
    setError(null);
    setBusy(true);
    try {
      await cerberus.mfaVerify(token, { code: code.trim() });
      setDone(true);
    } catch (e) {
      setError(errorMessage(e, "verification failed"));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthLayout title="MFA ativada">
        <Alert variant="success">
          MFA ativada. Da proxima vez, voce vai precisar do codigo.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Ativar MFA"
      subtitle="Escaneie o QR code com seu app autenticador e confirme com um codigo."
    >
      {error ? <Alert variant="error">{error}</Alert> : null}
      {!enrollment ? (
        <Alert variant="info">Carregando...</Alert>
      ) : (
        <>
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <QrCode dataUrl={enrollment.qr_data_url} />
          </div>
          <details style={{ marginBottom: "1rem" }}>
            <summary style={{ cursor: "pointer", fontSize: 14 }}>
              Codigos de backup ({enrollment.backup_codes.length})
            </summary>
            <ul
              style={{
                fontFamily: "monospace",
                fontSize: 13,
                background: "var(--bg)",
                padding: "0.75rem",
                borderRadius: 6,
                marginTop: 8,
              }}
            >
              {enrollment.backup_codes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <p style={{ fontSize: 12, color: "var(--muted)" }}>
              Guarde esses codigos em local seguro. Eles aparecem so uma vez.
            </p>
          </details>
          <form onSubmit={onVerify} noValidate>
            <FormField
              label="Codigo TOTP"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button type="submit" disabled={busy}>
              {busy ? "Verificando..." : "Confirmar"}
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
